<template>
    <modal size="lg" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="deduction">Edit {{ labels.noun }}</h3>
            <h3 class="modal-title" v-else>Add {{ labels.noun }}</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                    <!-- <div v-if="deduction"><span class="text-muted">Name: </span>{{ deduction.company_deduction_type.deduction_type.name }}</div> -->
                </div>
            </div>

            <div class="form-row">
                <!-- NB: loan mode swaps the deduction type for the enrollment. A loan is
                     repaid to the employee's own deferred comp plan, so it has to name
                     the one it belongs to -- and that is the only thing to choose. Which
                     deduction type the repayment is withheld under follows from the plan
                     (see DeferredCompPlan.loan_company_deduction_type), so it is not
                     shown at all: nobody adding a loan has a decision to make about it.
                     That leaves three inputs, which share the row evenly. -->
                <div v-if="isLoan" class="col col-12 col-md-4 pb-2">
                    <h5 class="table-header-like">{{ labels.deferredComp }}</h5>
                    <div v-if="deduction" class="pt-hf">
                        {{ deduction.employee_deferred_comp?.deferred_comp_plan?.display_name }}
                    </div>
                    <form-input v-else class="grid-input" v-model="formData.employee_deferred_comp" :errors="errors.employee_deferred_comp" type="select" :options="deferredCompOptions"/>
                </div>
                <div v-else class="col col-12 col-md-4 pb-2">
                    <h5 class="table-header-like">{{ labels.deductionType }}</h5>
                    <div v-if="deduction" class="pt-hf">
                        {{ deduction.company_deduction_type.name }}
                    </div>
                    <form-input v-else class="grid-input" v-model="formData.company_deduction_type" :errors="errors.company_deduction_type" type="select" :options="deductionTypeOptions"/>
                </div>
                <div class="col col-6 pb-2" :class="isNewLoan ? 'col-md-4' : 'col-md-2'">
                    <h5 class="table-header-like">{{ labels.startingAmount }}</h5>
                    <div v-if="deduction" class="pt-hf">
                        {{  $filters.dollars(deduction.amount) }}
                    </div>
                    <form-input v-else class="grid-input" v-model="formData.amount" :errors="errors.amount" type="currency"/>
                </div>
                <div v-if="deduction" class="col col-6 col-md-3 pb-2">
                    <h5 class="table-header-like">Current Balance</h5>
                    <form-input v-if="editingCurrentBalance" class="grid-input" v-model="formData.current_balance" :errors="errors.current_balance" type="currency"/>
                    <div v-else class="pt-hf">
                        {{ $filters.dollars(deduction.current_balance) }}
                        <a href @click.prevent="editCurrentBalance"><i class="far fa-pen edit-current-balance-btn" title="Edit"></i></a>
                    </div>
                </div>
                <div class="col col-6 pb-2" :class="isNewLoan ? 'col-md-4' : 'col-md-3'">
                    <h5 class="table-header-like">{{ labels.perPaycheckAmount }}</h5>
                    <form-input class="grid-input" v-model="formData.max_paycheck_amount" :errors="errors.max_paycheck_amount" type="currency"/>
                    <p class="field-helper-text">per pay period</p>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'

const DEFAULT_LABELS = {
    noun: 'Miscellaneous Deduction',
    deductionType: 'Deduction',
    startingAmount: 'Start Balance',
    perPaycheckAmount: 'Deduction Amount',
    deferredComp: 'Deferred Comp Plan',
}

export default {
    // NB: `deferredComps` is what puts this modal in loan mode -- pass the employee's
    // enrollments that can be borrowed against to add the plan picker, leave it off for
    // a plain misc deduction. It swaps the deduction type picker for the plan one: a
    // loan is withheld under the type its plan lends under, so there is nothing to pick.
    // `clientDeductionTypes` is therefore only read outside loan mode.
    props: ['client', 'employee', 'deduction', 'clientDeductionTypes', 'labelOverrides', 'deferredComps', ],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const data = {
            editingCurrentBalance: false,
            formData: this.makeFormData(),
            // NB: company_deduction_type is not here. It is required for a plain misc
            // deduction and derived for a loan, which only getFormRules() can tell apart.
            formRules: {
                amount: {presence: {allowEmpty: false}, },
                max_paycheck_amount: {presence: {allowEmpty: false}},
            },
            formLabels: {
                employee_deferred_comp: 'Deferred comp plan',
            },
        }

        return data
    },
    computed: {
        labels() {
            return Object.assign({}, DEFAULT_LABELS, this.labelOverrides || {})
        },
        isLoan() {
            return !!this.deferredComps
        },
        // Adding a loan is the one case with exactly three inputs, so they split the row
        // evenly. Everything else has a fourth column (the current balance) or narrower
        // read-only text in place of an input.
        isNewLoan() {
            return this.isLoan && !this.deduction
        },
        deferredCompOptions() {
            return (this.deferredComps || []).map(dc => {
                return {text: dc.deferred_comp_plan?.display_name, value: dc.id}
            }).sort((a, b) => a.text > b.text ? 1 : -1)
        },
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        deductionTypeOptions() {
            const result = []

            let cets = (this.clientDeductionTypes || []).filter(cet => cet.company_id == this.employee.company_id)
            // TODO KB ES if non-admin, further filter based on employee.position.deduction_types
            cets.forEach(cet => {
                // NB: the value is the CompanyDeductionType id, not the global DeductionType id
                result.push({text: cet.name, value: cet.id})
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
    },
    methods: {
        makeFormData() {
            const data = {
                amount: this.deduction ? this.deduction.amount : '',
                max_paycheck_amount: this.deduction ? this.deduction.max_paycheck_amount : '',
            }

            if (this.deferredComps) {
                // NB: submitted on edit too. The API refuses a change to it, but omitting
                // it would read as clearing it, which is the same refusal by a worse
                // message.
                data.employee_deferred_comp = this.deduction?.employee_deferred_comp_id ? this.deduction.employee_deferred_comp_id : ''
            } else {
                data.company_deduction_type = this.deduction?.company_deduction_type_id ? this.deduction.company_deduction_type_id : ''
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            if (!this.deduction) {
                if (this.isLoan) {
                    // The deduction type follows from the plan, so this is the only
                    // thing to choose.
                    rules.employee_deferred_comp = {presence: {allowEmpty: false}, }
                } else {
                    rules.company_deduction_type = {presence: {allowEmpty: false}, }
                }
            }
            return rules
        },
        validate() {
            let isValid = FormMixin.methods.validate.call(this)

            return isValid
        },
        editCurrentBalance() {
            this.formData.current_balance = this.deduction.current_balance
            this.editingCurrentBalance = true
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            let url
            if (this.deduction) {
                url = `/clients/${this.client.id}/employees/${this.employee.id}/misc-deductions/${this.deduction.id}`
            } else {
                url = `/clients/${this.client.id}/employees/${this.employee.id}/misc-deductions`
            }
            this.$store.dispatch('START_LOADING')
            return this.$api.post(url, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved deduction.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>
